import { FC, useEffect, useState } from 'react';
import { AiOutlineCalendar } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { TSTitleCard } from '../../../../../components/shared/card/Card';
import { Loader } from '../../../../../components/shared/Loader';
import { EmployeeSchedule } from '../../../../../helpers/BuildWorkingPattern';
import { dayIndexes } from '../../../../../helpers/dayIndexes';
import { ReadEmployee } from '../../../../../services/gql/graphql.generated';
import strings from '../../../team.translations';
import { useTeamMemberService } from '../../TeamMemberService';

interface TeamMemberAvailabilityScheduleCardProps {
  enableEdit?: boolean;
  employee: ReadEmployee;
}

export const TeamMemberAvailabilityScheduleCard: FC<TeamMemberAvailabilityScheduleCardProps> = ({
  employee,
  enableEdit
}) => {
  const [dataToRender, setDataToRender] = useState<any>();
  const navigate = useNavigate();

  // The backend is a bit weird and won't allow employees with this role to be changed.
  const isManager = employee.role === 'Manager';

  const getAvailabilitySchedule = useTeamMemberService(state => state.getAvailabilitySchedule);

  useEffect(() => {
    const get = async () => {
      const response = await getAvailabilitySchedule(employee.identityId);
      setDataToRender(response);
    };
    get();
  }, [employee.identityId, getAvailabilitySchedule]);

  const onEditClick = () => {
    navigate(`/team-member/${employee.identityId}/set-schedule`);
  };

  const groupedSchedule = (dataToRender as EmployeeSchedule[] | undefined)?.reduce<Record<number, EmployeeSchedule[]>>(
    (acc, item) => {
      const key = item.dayNumber ?? -1;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      acc[key].sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));
      return acc;
    },
    {}
  );

  const orderedSchedule = dayIndexes
    .map(dayNumber => groupedSchedule?.[dayNumber]?.[0])
    .filter(Boolean) as EmployeeSchedule[];

  return (
    <TSTitleCard
      primaryAction={
        enableEdit && !isManager
          ? {
              label: strings.scheduleCard.editLabel,
              onClick: onEditClick
            }
          : undefined
      }
      title={strings.scheduleCard.cardTitle}
      icon={AiOutlineCalendar}
    >
      <div className="grid grid-flow-row grid-cols-1 sm:grid-cols-2 sm:grid-flow-col sm:grid-rows-4 gap-y-5 gap-x-8 lg:max-w-6xl lg:mx-auto lg:px-8">
        {groupedSchedule ? (
          orderedSchedule.map((item: EmployeeSchedule) => {
            const periods = groupedSchedule[item.dayNumber ?? -1]?.filter(period => period.available) ?? [];
            return (
              <div
                key={item.dayNumber}
                className="grid grid-cols-2 gap-x-6 items-start"
                style={{ gridTemplateColumns: '0.8fr 1.2fr' }}
              >
                <p className="text-right font-semibold sm:px-0">{item.day}</p>
                <p className="break-keep">
                  {periods.length > 0
                    ? periods.map(period => `${period.start} - ${period.end}`).join(', ')
                    : strings.scheduleCard.notAvailable}
                </p>
              </div>
            );
          })
        ) : (
          <Loader className="h-10 mx-auto col-span-full" />
        )}
      </div>
    </TSTitleCard>
  );
};
